/**
 * SendMultiSMSModal Component
 * Modal for sending the same SMS message to multiple subcontractors at once
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Send, Users, Phone, Loader2, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { sendSMS } from '../services/sms'
import { getTemplates } from '../services/smsTemplates'

interface Recipient {
  id: string
  name: string
  phone: string
}

interface SendMultiSMSModalProps {
  isOpen: boolean
  onClose: () => void
  recipients: Recipient[]
  assignmentId?: string
  onMessagesSent?: () => void
}

// Template variable definitions
const TEMPLATE_VARIABLES = [
  { key: '{name}', description: 'Mottagarens förnamn' },
  { key: '{fullname}', description: 'Mottagarens fullständiga namn' },
  { key: '{avsändare}', description: 'Ditt namn (inloggad användare)' },
  { key: '{uppdragsnr}', description: 'Uppdragsnummer (t.ex. #1001)' },
  { key: '{datum}', description: 'Datum (t.ex. 2025-12-26)' },
  { key: '{tid}', description: 'Starttid' },
  { key: '{location}', description: 'Uppdragets plats' },
]

export function SendMultiSMSModal({
  isOpen,
  onClose,
  recipients,
  assignmentId,
  onMessagesSent
}: SendMultiSMSModalProps) {
  const { profile } = useAuth()
  const [message, setMessage] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [showVariables, setShowVariables] = useState(false)
  const [sendProgress, setSendProgress] = useState<{ sent: number; total: number } | null>(null)
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set())

  // Initialize all recipients as selected
  useEffect(() => {
    if (isOpen) {
      setSelectedRecipients(new Set(recipients.filter(r => r.phone).map(r => r.id)))
    }
  }, [isOpen, recipients])

  // Fetch templates from database
  const { data: dbTemplates = [] } = useQuery({
    queryKey: ['sms-templates', 'subcontractor'],
    queryFn: () => getTemplates('subcontractor'),
    enabled: isOpen
  })

  // Fetch assignment data if assignmentId is provided
  const { data: assignment } = useQuery({
    queryKey: ['assignment-sms', assignmentId],
    queryFn: async () => {
      if (!assignmentId) return null
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          start_date,
          start_time,
          location
        `)
        .eq('id', assignmentId)
        .single()

      if (error) {
        console.error('Error fetching assignment:', error)
        return null
      }
      return data
    },
    enabled: isOpen && !!assignmentId
  })

  const templates = dbTemplates.map(t => ({ id: t.id, name: t.name, content: t.content }))

  const SMS_CHAR_LIMIT = 160
  const SMS_EXTENDED_LIMIT = 480

  // Replace template variables with actual values for a specific recipient
  const replaceTemplateVariables = (text: string, recipient: Recipient): string => {
    let result = text

    // Recipient-specific variables
    const firstName = recipient.name.split(' ')[0]
    result = result.replace(/{name}/g, firstName)
    result = result.replace(/{fullname}/g, recipient.name)

    // Sender name
    const senderName = profile?.full_name || ''
    const senderFirstName = senderName.split(' ')[0]
    result = result.replace(/{avsändare}/g, senderFirstName)

    // Assignment-specific variables
    if (assignment) {
      const assignmentNumber = assignment.assignment_number
        ? `#${assignment.assignment_number}`
        : ''
      result = result.replace(/{uppdragsnr}/g, assignmentNumber)
      result = result.replace(/{datum}/g, assignment.start_date || '')
      result = result.replace(/{tid}/g, assignment.start_time || '')
      result = result.replace(/{location}/g, assignment.location || '')
    }

    return result
  }

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId)
    const template = templates.find(t => t.id === templateId)
    if (template) {
      setMessage(template.content)
    }
  }

  const toggleRecipient = (recipientId: string) => {
    const newSelected = new Set(selectedRecipients)
    if (newSelected.has(recipientId)) {
      newSelected.delete(recipientId)
    } else {
      newSelected.add(recipientId)
    }
    setSelectedRecipients(newSelected)
  }

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Skriv ett meddelande')
      return
    }

    const recipientsToSend = recipients.filter(r => selectedRecipients.has(r.id) && r.phone)
    if (recipientsToSend.length === 0) {
      toast.error('Välj minst en mottagare med telefonnummer')
      return
    }

    setIsSending(true)
    setSendProgress({ sent: 0, total: recipientsToSend.length })

    let successCount = 0
    let failCount = 0

    for (let i = 0; i < recipientsToSend.length; i++) {
      const recipient = recipientsToSend[i]
      const personalizedMessage = replaceTemplateVariables(message, recipient)

      try {
        const result = await sendSMS({
          message: personalizedMessage,
          recipient_type: 'subcontractor',
          recipient_id: recipient.id,
          recipient_name: recipient.name,
          recipient_phone: recipient.phone,
          assignment_id: assignmentId
        })

        if (result.success) {
          successCount++
        } else {
          failCount++
          console.error(`Failed to send to ${recipient.name}:`, result.error)
        }
      } catch (err) {
        failCount++
        console.error(`Error sending to ${recipient.name}:`, err)
      }

      setSendProgress({ sent: i + 1, total: recipientsToSend.length })
    }

    setIsSending(false)
    setSendProgress(null)

    if (failCount === 0) {
      toast.success(`SMS skickat till ${successCount} trädgårdsmästare`)
      onMessagesSent?.()
      onClose()
      setMessage('')
      setSelectedTemplate(null)
    } else if (successCount > 0) {
      toast.success(`SMS skickat till ${successCount} av ${recipientsToSend.length}`)
      toast.error(`${failCount} misslyckades`)
      onMessagesSent?.()
    } else {
      toast.error('Kunde inte skicka några SMS')
    }
  }

  if (!isOpen) return null

  const recipientsWithPhone = recipients.filter(r => r.phone)
  const selectedCount = recipientsWithPhone.filter(r => selectedRecipients.has(r.id)).length

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary-600" />
            <h2 className="text-base sm:text-lg font-semibold text-nature-900 dark:text-nature-100">
              SMS till alla TG
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 rounded bg-nature-100 dark:bg-nature-700 sm:bg-transparent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4">
          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-2">
              Mottagare ({selectedCount} av {recipientsWithPhone.length} valda)
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto p-2 bg-nature-50 dark:bg-nature-700/50 rounded-lg">
              {recipients.map(recipient => (
                <label
                  key={recipient.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    recipient.phone
                      ? 'hover:bg-nature-100 dark:hover:bg-nature-600'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedRecipients.has(recipient.id)}
                    onChange={() => toggleRecipient(recipient.id)}
                    disabled={!recipient.phone}
                    className="rounded border-nature-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-nature-900 dark:text-nature-100 flex-1">
                    {recipient.name}
                  </span>
                  {recipient.phone ? (
                    <span className="text-xs text-nature-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {recipient.phone}
                    </span>
                  ) : (
                    <span className="text-xs text-red-500">Saknar telefon</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Template selector */}
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                Välj mall (valfritt)
              </label>
              <select
                value={selectedTemplate || ''}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-nature-300 dark:border-nature-600 bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
              >
                <option value="">Ingen mall</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Message input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
                Meddelande
              </label>
              <button
                type="button"
                onClick={() => setShowVariables(!showVariables)}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Info className="h-3 w-3" />
                {showVariables ? 'Dölj variabler' : 'Visa variabler'}
              </button>
            </div>

            {showVariables && (
              <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs">
                <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Tillgängliga variabler:
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {TEMPLATE_VARIABLES.map(v => (
                    <div key={v.key} className="text-blue-700 dark:text-blue-300">
                      <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{v.key}</code>
                      <span className="ml-1 text-blue-600 dark:text-blue-400">{v.description}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-blue-600 dark:text-blue-400 italic">
                  {'{name}'} ersätts med varje mottagares förnamn
                </p>
              </div>
            )}

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Skriv ditt meddelande här..."
              className="w-full px-3 py-2 rounded-lg border border-nature-300 dark:border-nature-600 bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 resize-none"
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={`${
                message.length > SMS_EXTENDED_LIMIT
                  ? 'text-red-500'
                  : message.length > SMS_CHAR_LIMIT
                    ? 'text-amber-500'
                    : 'text-nature-500'
              }`}>
                {message.length} tecken
                {message.length > SMS_CHAR_LIMIT && ` (${Math.ceil(message.length / SMS_CHAR_LIMIT)} SMS)`}
              </span>
              <span className="text-nature-400">
                Max {SMS_EXTENDED_LIMIT} tecken
              </span>
            </div>
          </div>

          {/* Preview */}
          {message && recipientsWithPhone.length > 0 && (
            <div className="p-3 bg-nature-50 dark:bg-nature-700/50 rounded-lg">
              <p className="text-xs font-medium text-nature-500 dark:text-nature-400 mb-1">
                Förhandsvisning (för {recipientsWithPhone[0].name}):
              </p>
              <p className="text-sm text-nature-700 dark:text-nature-300 whitespace-pre-wrap">
                {replaceTemplateVariables(message, recipientsWithPhone[0])}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-nature-200 dark:border-nature-700 flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            disabled={isSending}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
          >
            Avbryt
          </button>

          <button
            onClick={handleSend}
            disabled={isSending || !message.trim() || selectedCount === 0}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-primary-600 hover:bg-primary-700 disabled:bg-nature-300 dark:disabled:bg-nature-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {sendProgress ? `${sendProgress.sent}/${sendProgress.total}` : 'Skickar...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Skicka till {selectedCount} {selectedCount === 1 ? 'person' : 'pers'}</span>
                <span className="sm:hidden">Skicka ({selectedCount})</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
